import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, LoadingState, Button } from '../ui';
import { ArrowLeftIcon, ArrowRightCircleIcon } from '@heroicons/react/24/outline';

// Hooks
import useProjectData from './hooks/useProjectData';
import useKeywords from './hooks/useKeywords';
import useGroups from './hooks/useGroups';

// Components
import ProjectForm from './ProjectForm';
import KeywordCategoriesManager from './KeywordCategoriesManager';
import PreissueRelevanceManager from './PreissueRelevanceManager';
import ProjectAutoAssignmentEnhanced from '../ProjectAutoAssignmentEnhanced';
import ProjectStatistics from './ProjectStatistics';
import ProjectAgentsManager from './ProjectAgentsManager';
import ActivityReviewQueue from './ActivityReviewQueue';
import GroupsManager from './GroupsManager';
import MigrateToForumModal from './MigrateToForumModal';
import ProjectTestRunPanel from './ProjectTestRunPanel';

// Dialogs
import TestKeywordsDialog from './dialogs/TestKeywordsDialog';
import AddGroupsDialog from './dialogs/AddGroupsDialog';
import BulkRemoveDialog from './dialogs/BulkRemoveDialog';

function buildFilterDescription(filters) {
  if (!filters) {
    return '';
  }
  const parts = [];
  if (filters.search) {
    parts.push(`search "${filters.search}"`);
  }
  if (filters.category) {
    parts.push(`category=${filters.category}`);
  }
  if (filters.language) {
    parts.push(`language=${filters.language}`);
  }
  if (filters.country) {
    parts.push(`country=${filters.country}`);
  }
  if (filters.assignment_type) {
    parts.push(`source=${filters.assignment_type}`);
  }
  return parts.join(', ');
}

const ProjectDetailPage = () => {
  const [mainTab, setMainTab] = useState('keywords');
  const [keywordTab] = useState('legacy'); // 'legacy', 'advanced'
  const [migrateModalOpen, setMigrateModalOpen] = useState(false);
  
  // Project data hook
  const {
    project,
    setProject,
    loading,
    saving,
    assigningHumanAdmin,
    revokingHumanAdmin,
    error,
    success,
    setError,
    setSuccess,
    isNewProject,
    projectId,
    handleSave,
    handleAssignHumanAdmin,
    handleRevokeHumanAdmin
  } = useProjectData();

  const groupsHandlers = useGroups(projectId, setError, setSuccess);
  
  // Keywords hook
  const keywordsHandlers = useKeywords(project, setProject, setError);
  const {
    testKeywordDialogOpen,
    testMessage,
    setTestMessage,
    testResults,
    handleCloseTestKeywordDialog,
    handleTestKeyword
  } = keywordsHandlers;
  
  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProject({ ...project, [name]: value });
  };
  
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setProject({ ...project, [name]: checked });
  };

  const handleTestRunStatusChange = (status) => {
    setProject((current) => ({
      ...current,
      is_active: status.is_active,
      test_run_status: status.status,
      test_run_matched_count: status.matched_count,
      test_run_number: status.run_number,
      keyword_config_revision: status.keyword_config_revision,
    }));
  };
  
  
  if (loading) {
    return <LoadingState message="Loading project data..." />;
  }
  
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center">
          <Link to="/projects" className="mr-4 text-gray-500 hover:text-gray-700">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-semibold text-gray-900">
            {isNewProject ? 'Create New Project' : 'Project Details'}
          </h1>
        </div>
        {!isNewProject && project && !(project.is_forum === true && project.owner_account_id != null && project.invite_link) && (
          <Button
            variant="secondary"
            icon={ArrowRightCircleIcon}
            onClick={() => setMigrateModalOpen(true)}
            title="Migrate this legacy project to a forum"
          >
            Move to Forum
          </Button>
        )}
      </div>
      
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert variant="success" className="mb-4">
          {success}
        </Alert>
      )}
      
      <ProjectForm
        project={project}
        isNewProject={isNewProject}
        saving={saving}
        assigningHumanAdmin={assigningHumanAdmin}
        revokingHumanAdmin={revokingHumanAdmin}
        onInputChange={handleInputChange}
        onSwitchChange={handleSwitchChange}
        onSave={handleSave}
        onAssignHumanAdmin={handleAssignHumanAdmin}
        onRevokeHumanAdmin={handleRevokeHumanAdmin}
      />

      <ProjectTestRunPanel
        project={project}
        isNewProject={isNewProject}
        onStatusChange={handleTestRunStatusChange}
      />
      
      {/* Main content with tabs */}
      <div className="mb-6">
        <div className="overflow-x-auto border-b border-gray-200">
          <nav className="-mb-px flex min-w-max sm:min-w-0" aria-label="Main Tabs">
            <button
              onClick={() => setMainTab('keywords')}
              className={`${
                mainTab === 'keywords'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } min-h-[44px] min-w-[120px] flex-none px-3 py-2 text-center text-sm font-medium sm:min-w-0 sm:flex-1 border-b-2`}
            >
              Categories
            </button>
            <button
              onClick={() => setMainTab('relevance')}
              className={`${
                mainTab === 'relevance'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } min-h-[44px] min-w-[120px] flex-none px-3 py-2 text-center text-sm font-medium sm:min-w-0 sm:flex-1 border-b-2`}
              disabled={isNewProject}
            >
              Relevance
            </button>
            <button
              onClick={() => setMainTab('auto-assignment')}
              className={`${
                mainTab === 'auto-assignment'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } min-h-[44px] min-w-[120px] flex-none px-3 py-2 text-center text-sm font-medium sm:min-w-0 sm:flex-1 border-b-2`}
              disabled={isNewProject}
            >
              Auto Assignment
            </button>
            <button
              onClick={() => setMainTab('groups')}
              className={`${
                mainTab === 'groups'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } min-h-[44px] min-w-[120px] flex-none px-3 py-2 text-center text-sm font-medium sm:min-w-0 sm:flex-1 border-b-2`}
              disabled={isNewProject}
            >
              Groups
            </button>
            <button
              onClick={() => setMainTab('agents')}
              className={`${
                mainTab === 'agents'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } min-h-[44px] min-w-[120px] flex-none px-3 py-2 text-center text-sm font-medium sm:min-w-0 sm:flex-1 border-b-2`}
              disabled={isNewProject}
            >
              Agents
            </button>
            <button
              onClick={() => setMainTab('activity-review')}
              className={`${
                mainTab === 'activity-review'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } min-h-[44px] min-w-[120px] flex-none px-3 py-2 text-center text-sm font-medium sm:min-w-0 sm:flex-1 border-b-2`}
              disabled={isNewProject}
            >
              Activity review
            </button>
            <button
              onClick={() => setMainTab('statistics')}
              className={`${
                mainTab === 'statistics'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } min-h-[44px] min-w-[120px] flex-none px-3 py-2 text-center text-sm font-medium sm:min-w-0 sm:flex-1 border-b-2`}
              disabled={isNewProject}
            >
              Statistics
            </button>
          </nav>
        </div>

        {mainTab === 'keywords' ? (
          <KeywordCategoriesManager
            project={project}
            isNewProject={isNewProject}
          />
        ) : mainTab === 'relevance' ? (
          <PreissueRelevanceManager projectId={projectId} />
        ) : mainTab === 'auto-assignment' ? (
          <div className="mt-4">
            <ProjectAutoAssignmentEnhanced projectId={projectId} />
          </div>
        ) : mainTab === 'groups' ? (
          <div className="mt-4">
            <GroupsManager
              chats={groupsHandlers.chats}
              selectedChats={groupsHandlers.selectedChats}
              groupsHandlers={groupsHandlers}
              groupsLoading={groupsHandlers.groupsLoading}
              isNewProject={isNewProject}
              chatStats={groupsHandlers.chatStats}
              pagination={groupsHandlers.pagination}
              pageSize={groupsHandlers.pageSize}
              onPageChange={groupsHandlers.handlePageChange}
              onPageSizeChange={groupsHandlers.handlePageSizeChange}
            />
          </div>
        ) : mainTab === 'agents' ? (
          <ProjectAgentsManager projectId={projectId} />
        ) : mainTab === 'activity-review' ? (
          <ActivityReviewQueue projectId={projectId} />
        ) : (
          <ProjectStatistics projectId={projectId} />
        )}
      </div>
      
      {/* Dialogs */}
      <TestKeywordsDialog
        isOpen={testKeywordDialogOpen}
        onClose={handleCloseTestKeywordDialog}
        testMessage={testMessage}
        setTestMessage={setTestMessage}
        testResults={testResults}
        onTestKeyword={handleTestKeyword}
        keywordTab={keywordTab}
      />

      <AddGroupsDialog
        isOpen={groupsHandlers.addGroupsDialogOpen}
        onClose={groupsHandlers.handleCloseAddGroupsDialog}
        excludeChatIds={groupsHandlers.chats.map((chat) => chat.chat_id)}
        selectedGroups={groupsHandlers.selectedGroups}
        onGroupSelection={groupsHandlers.handleGroupSelection}
        onAddGroups={groupsHandlers.handleAddGroups}
        projectId={projectId}
      />

      <BulkRemoveDialog
        isOpen={groupsHandlers.bulkRemoveDialogOpen}
        onClose={groupsHandlers.handleCloseBulkRemoveDialog}
        mode={groupsHandlers.bulkRemoveMode?.mode || 'selected'}
        selectedChatsCount={groupsHandlers.selectedChats.length}
        filterMatchCount={groupsHandlers.bulkRemoveMode?.count || 0}
        filterDescription={buildFilterDescription(groupsHandlers.filters)}
        onConfirmRemove={groupsHandlers.handleBulkRemoveChats}
      />

      {migrateModalOpen && (
        <MigrateToForumModal
          projectId={projectId}
          project={project}
          onClose={() => setMigrateModalOpen(false)}
          onDone={() => {
            // Reload project data to reflect any changes
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

export default ProjectDetailPage;
